import { ChangeEvent, useEffect, useState } from 'react';
import { UPLOAD_INPUT_ACCEPT_ATTRIBUTE } from '../../../constants/videoUploadAcceptTypes';
import { toastError, toastSuccess } from '../../../libs/toasts/toasts';
import { Text, useTheme } from '@gravity-ui/uikit';
import { errorsT, videoUploadT } from '../../../i18n';
import { filterValidVideoFiles } from '../utils/videoFiles';
import '../styles/index.css';

type DragAndDropProps = {
    onSuccess: (files: File[]) => void;
    inputRef?: React.RefObject<HTMLInputElement>;
    multiple?: boolean;
};

export function DragAndDrop({ onSuccess, inputRef, multiple = false }: DragAndDropProps) {
    const [drag, setDrag] = useState(false);
    const theme = useTheme();

    const handleDragOver = (event: DragEvent) => {
        event.preventDefault();

        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }

        setDrag(true);
    };

    const handleDragLeave = (event: DragEvent) => {
        const relatedTarget = event.relatedTarget as Node | null;

        // https://bugs.chromium.org/p/chromium/issues/detail?id=68629
        if (
            relatedTarget?.parentElement === null ||
            (event.currentTarget as Node).contains(relatedTarget)
        ) {
            return;
        }

        setDrag(false);
    };

    const handleDrop = async (event: DragEvent) => {
        event.preventDefault();

        if (event.dataTransfer) {
            const droppedFiles: File[] = [];
            for (let i = 0; i < event.dataTransfer.files.length; i++) {
                droppedFiles.push(event.dataTransfer.files[i]);
            }

            setDrag(false);

            if (droppedFiles.length === 0) {
                return;
            }

            const { validFiles, invalidCount } = await filterValidVideoFiles(droppedFiles);

            if (invalidCount > 0) {
                if (invalidCount === droppedFiles.length) {
                    toastError.open(errorsT('invalidFileType'));
                    return;
                } else {
                    toastError.open(errorsT('filesSkippedFormat', { count: invalidCount }));
                }
            }

            if (validFiles.length > 0) {
                if (multiple) {
                    onSuccess(validFiles);
                    if (validFiles.length > 1) {
                        toastSuccess.open(videoUploadT('filesAdded', { count: validFiles.length }));
                    }
                } else {
                    onSuccess([validFiles[0]]);
                    if (validFiles.length > 1) {
                        toastError.open(errorsT('onlyOneFileAllowed'));
                    }
                }
            }
        }
    };

    const onChangeHandler = async (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const fileList = event.target.files;

            const files: File[] = [];
            for (let i = 0; i < fileList.length; i++) {
                files.push(fileList[i]);
            }

            const { validFiles, invalidCount } = await filterValidVideoFiles(files);

            if (invalidCount > 0) {
                if (invalidCount === files.length) {
                    toastError.open(errorsT('invalidFileType'));
                    return;
                } else {
                    toastError.open(errorsT('filesSkippedFormatAlt', { count: invalidCount }));
                }
            }

            if (validFiles.length > 0) {
                if (multiple) {
                    onSuccess(validFiles);
                    if (validFiles.length > 1) {
                        toastSuccess.open(videoUploadT('filesAdded', { count: validFiles.length }));
                    }
                } else {
                    onSuccess([validFiles[0]]);
                }
            }
        }
    };

    useEffect(() => {
        document.documentElement.addEventListener('dragover', handleDragOver);
        document.documentElement.addEventListener('drop', handleDrop);
        document.documentElement.addEventListener('dragleave', handleDragLeave);

        return () => {
            document.documentElement.removeEventListener('dragover', handleDragOver);
            document.documentElement.removeEventListener('drop', handleDrop);
            document.documentElement.removeEventListener('dragleave', handleDragLeave);
        };
    }, [multiple]);

    return (
        <div className={drag ? 'dnd-active' : ''}>
            {drag && (
                <Text
                    variant="subheader-3"
                    color={theme === 'dark' ? 'primary' : 'inverted-primary'}
                >
                    {multiple
                        ? videoUploadT('dragAndDropActiveMultiple')
                        : videoUploadT('dragAndDropActiveSingle')}
                </Text>
            )}

            <input
                type="file"
                id="uploadFile"
                accept={UPLOAD_INPUT_ACCEPT_ATTRIBUTE}
                ref={inputRef}
                hidden
                multiple={multiple}
                onChange={onChangeHandler}
            />
            <label htmlFor="uploadFile" hidden>
                {videoUploadT('dragAndDropSingle')}
            </label>
        </div>
    );
}
